import { yupResolver } from '@hookform/resolvers/yup';
import { Checkbox, SelectedFileStatus, Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { FormDialog, FormDropZone } from '@vizit/forms';
import { InfoCircleIcon } from '@vizit/icons';
import { plural } from '@vizit/utils';
import * as React from 'react';
import { type Control, useController, useForm, useWatch } from 'react-hook-form';
import * as yup from 'yup';

type BenchmarkUploadFormSchema = {
  files: File[];
  rescoreAcknowledged: boolean;
};

const EMPTY_FORM = { files: undefined as unknown as File[], rescoreAcknowledged: false };

function fileExtension(file: File) {
  const [, extension = ''] = /\.([^.]+)$/.exec(file.name) ?? [];
  return extension.toLowerCase();
}

function filesWithUnsupportedNames(files: File[], allowedExtensions?: readonly string[]) {
  if (!allowedExtensions) {
    return [];
  }
  return files.filter((file) => !allowedExtensions.includes(fileExtension(file)));
}

function listExtensions(extensions: readonly string[]) {
  const names = extensions.map((extension) => `.${extension}`);
  return `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
}

function buildSchema(requiresRescoreAcknowledgement: boolean, allowedExtensions?: readonly string[]) {
  return yup.object().shape({
    files: yup
      .array()
      .of(yup.mixed<File>().required())
      .min(1, 'Add at least one image')
      .required('Add at least one image')
      .test('file-name-extension', 'Some file names have an unsupported extension', (files) =>
        files ? filesWithUnsupportedNames(files, allowedExtensions).length === 0 : true,
      ),
    rescoreAcknowledged: requiresRescoreAcknowledgement
      ? yup.boolean().oneOf([true], 'Confirm the rescore to upload').required()
      : yup.boolean().defined(),
  });
}

function RescoreNotice({ count, control }: { count: number; control: Control<BenchmarkUploadFormSchema> }) {
  const { field } = useController({ name: 'rescoreAcknowledged', control });
  const collections = plural(count, 'collection');

  return (
    <div className="flex flex-col gap-4 border border-blue-200 bg-blue-50 p-6">
      <div className="flex items-center gap-3 text-blue-700">
        <InfoCircleIcon size={16} aria-hidden />
        <Text size={TextSize.Sm} weight={TextWeight.Semibold} className="text-inherit">
          Rescores {collections}
        </Text>
      </div>
      <Text size={TextSize.Sm} color={TextColor.Secondary} showEllipsis={false}>
        {count === 1 ? 'A collection is' : 'These collections are'} scored against this benchmark. New images change the
        scores, so {count === 1 ? 'it is' : 'they are'} rescored once the upload finishes.
      </Text>
      {/* The atom sets the label's text style; the whole row is its label, so any of it ticks the box. */}
      <Checkbox isSelected={field.value} onChange={field.onChange} onBlur={field.onBlur} wrapLabel>
        {`I understand this will rescore ${collections}`}
      </Checkbox>
    </div>
  );
}

export interface BenchmarkUploadDialogProps {
  /**
   * Whether the dialog is open
   */
  isOpen: boolean;
  /**
   * Called when the dialog opens or closes
   */
  onOpenChange: (isOpen: boolean) => void;
  /**
   * Number of collections scored against the benchmark. Above zero, uploading waits for the user to acknowledge
   * that they will be rescored; the API rescores them either way. Leave unset while the count is unknown.
   *
   * @optional
   */
  rescoreCollectionCount?: number;
  /**
   * File name extensions the upload accepts, lowercase and without the dot. Files named otherwise are listed and
   * block the upload. Leave unset to accept any name the drop zone takes.
   *
   * @optional
   */
  allowedExtensions?: readonly string[];
  /**
   * Called with the chosen files; the dialog closes right after
   */
  onSubmit: (files: File[]) => void;
}

/**
 * Upload images to a benchmark: drop or browse PNG, JPEG or WEBP files up to 25 MB each, review the queue, then
 * "Upload N". When collections already use the benchmark, a notice says they will be rescored and uploading waits
 * for the user to acknowledge it.
 */
export function BenchmarkUploadDialog(props: BenchmarkUploadDialogProps) {
  const { isOpen, onOpenChange, rescoreCollectionCount = 0, allowedExtensions, onSubmit } = props;
  const requiresRescoreAcknowledgement = rescoreCollectionCount > 0;

  const schema = React.useMemo(
    () => buildSchema(requiresRescoreAcknowledgement, allowedExtensions),
    [requiresRescoreAcknowledgement, allowedExtensions],
  );

  const formMethods = useForm<BenchmarkUploadFormSchema>({
    resolver: yupResolver(schema) as never,
    defaultValues: EMPTY_FORM,
    mode: 'all',
  });
  const { control, handleSubmit, reset, setError } = formMethods;

  const files = useWatch({ control, name: 'files' }) ?? [];
  const unsupportedFiles = filesWithUnsupportedNames(files, allowedExtensions);

  const handleFormSubmit = (data: BenchmarkUploadFormSchema) => {
    onSubmit(data.files);
    // FormDialog clears the form only when it closes itself (Cancel, Escape, a click outside), and the drop zone's
    // list starts over when the dialog reopens: left filled, the form would offer to send these files again.
    reset(EMPTY_FORM);
    onOpenChange(false);
  };

  return (
    <FormDialog
      formMethods={formMethods}
      formName="benchmark-upload"
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      onSubmit={handleSubmit(handleFormSubmit)}
      onResetForm={() => reset(EMPTY_FORM)}
    >
      <FormDialog.Title>Upload images</FormDialog.Title>
      <FormDialog.Subtitle>
        Add one or more reference images to this benchmark. Pick a product category to score them.
      </FormDialog.Subtitle>
      <FormDialog.FormFields>
        <div className="flex flex-col gap-8">
          <FormDropZone
            control={control}
            name="files"
            allowsMultiple
            uploadStatus={SelectedFileStatus.Ready}
            onFileValidationError={(error) => error && setError('files', { type: 'custom', message: error })}
          />
          {allowedExtensions && unsupportedFiles.length > 0 && (
            <Text role="alert" size={TextSize.Sm} className="text-error-primary" showEllipsis={false}>
              Rename {unsupportedFiles.map((file) => file.name).join(', ')} to end in{' '}
              {listExtensions(allowedExtensions)}, or remove {unsupportedFiles.length === 1 ? 'it' : 'them'}. Until the
              benchmark has a product category, images are read by their file name.
            </Text>
          )}
          {requiresRescoreAcknowledgement && <RescoreNotice count={rescoreCollectionCount} control={control} />}
        </div>
      </FormDialog.FormFields>
      <FormDialog.SubmitButton>{files.length > 0 ? `Upload ${files.length}` : 'Upload'}</FormDialog.SubmitButton>
    </FormDialog>
  );
}
